import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { container } from "tsyringe";
import PostCard from "../../Forum/PostCard";
import { EPostCardModal, usePostCardModal } from "../../Forum/PostCardModal";
import CreatePostModal from "../../Forum/PostCardModal/CreatePostModal";
import EditPostModal from "../../Forum/PostCardModal/EditPostModal";
import DeletePostModal from "../../Forum/PostCardModal/DeletePostModal";
import { EForumPostCategory } from "common/enums/Project/Config/Forum/EForumPostCategory";
import ForumPostResponseResource from "common/resources/Project/Config/Forum/ForumPostResponseResource";
import { upsert } from "common/utils/array";
import { useProjectId } from "hooks/useProjectId";
import ForumPostService from "services/projects/ForumPostService";
import classes from "./classes.module.scss";

type IProps = {};

const forumPostService = container.resolve(ForumPostService);

export const CastingForum = forwardRef<{ onOpenCreateModal: () => void }, IProps>((_props, ref) => {
	const { posts, removePost, upsertPost } = useCastingPosts();
	const { selectedPost, isModalOpen, onCloseModal, onOpenCreateModal, onOpenEditModal, onOpenDeleteModal } = usePostCardModal();

	useImperativeHandle(ref, () => ({
		onOpenCreateModal,
	}));

	const onCreated = useCallback(
		(post: ForumPostResponseResource) => {
			upsertPost(post);
			onCloseModal();
		},
		[onCloseModal, upsertPost],
	);

	const onEdited = useCallback(
		(post: ForumPostResponseResource) => {
			upsertPost(post);
			onCloseModal();
		},
		[onCloseModal, upsertPost],
	);

	const onDeleted = useCallback(
		(deletedPost: Pick<ForumPostResponseResource, "id">) => {
			removePost(deletedPost);
			onCloseModal();
		},
		[onCloseModal, removePost],
	);

	return (
		<div className={classes["root"]}>
			<div className={classes["posts-container"]}>
				{posts.map((post) => (
					<PostCard key={post.id} post={post} onEditClick={() => onOpenEditModal(post)} onDeleteClick={() => onOpenDeleteModal(post)} />
				))}
			</div>

			{isModalOpen(EPostCardModal.CREATE_POST) && <CreatePostModal isOpen category={EForumPostCategory.CASTING} onClose={onCloseModal} onCreated={onCreated} />}
			{isModalOpen(EPostCardModal.EDIT_POST) && selectedPost && <EditPostModal isOpen post={selectedPost} onClose={onCloseModal} onEdited={onEdited} />}
			{isModalOpen(EPostCardModal.DELETE_POST) && selectedPost && <DeletePostModal isOpen post={selectedPost} onClose={onCloseModal} onDeleted={onDeleted} />}
		</div>
	);
});

const useCastingPosts = () => {
	const { projectId } = useProjectId();
	const [posts, setPosts] = useState<ForumPostResponseResource[]>([]);

	const fetchPosts = useCallback(() => {
		return forumPostService
			.getCastingPosts(projectId)
			.then(setPosts)
			.catch((error) => {
				console.error(error);
				setPosts([]);
			});
	}, [projectId]);

	const upsertPost = useCallback((post: ForumPostResponseResource) => {
		setPosts((posts) => upsert(posts, post, (p) => p.id === post.id));
	}, []);

	const removePost = useCallback((deletedPost: Pick<ForumPostResponseResource, "id">) => {
		setPosts((posts) => posts.filter((p) => p.id !== deletedPost.id));
	}, []);

	useEffect(() => {
		fetchPosts();
	}, [fetchPosts]);

	return { posts, removePost, upsertPost };
};
